'use client';
import React from 'react';

import type { UseFormReturn } from 'react-hook-form';
import type { SignUpFormData } from './SellerSignUpForm';
import Icon from '@/components/ui/AppIcon';

interface Props {
  form: UseFormReturn<SignUpFormData>;
}

const BUSINESS_TYPES = [
  { value: '', label: 'Select business type' },
  { value: 'individual', label: 'Individual' },
  { value: 'proprietorship', label: 'Sole Proprietorship' },
  { value: 'partnership', label: 'Partnership Firm' },
  { value: 'pvt_ltd', label: 'Private Limited Company' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
];

const INDIAN_STATES = [
  '', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry',
];

export default function Step3Business({ form }: Props) {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">
          Business & Banking Details
        </h2>
        <p className="text-sm text-muted-foreground">
          Required for KYC verification and receiving payouts. All information
          is encrypted and secure.
        </p>
      </div>

      {/* Business Info Group */}
      <div className="bg-muted/30 rounded-xl p-4 border border-border">
        <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
          <Icon name="BuildingOfficeIcon" size={16} className="text-primary" />
          Business Information
        </h3>
        <div className="space-y-4">
          {/* Business Name */}
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Business / Legal Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              placeholder="As registered with government"
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
              {...register('businessName', {
                required: 'Business name is required',
              })}
            />
            {errors.businessName && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.businessName.message}
              </p>
            )}
          </div>

          {/* Business Type */}
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Business Type <span className="text-error">*</span>
            </label>
            <select
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus"
              {...register('businessType', {
                required: 'Please select a business type',
              })}
            >
              {BUSINESS_TYPES.map((bt) => (
                <option key={`bt-${bt.value}`} value={bt.value}>
                  {bt.label}
                </option>
              ))}
            </select>
            {errors.businessType && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.businessType.message}
              </p>
            )}
          </div>

          {/* PAN + GST */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                PAN Number <span className="text-error">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                10-character PAN (e.g. ABCDE1234F)
              </p>
              <input
                type="text"
                placeholder="ABCDE1234F"
                maxLength={10}
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground uppercase"
                {...register('panNumber', {
                  required: 'PAN number is required',
                  pattern: {
                    value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                    message: 'Enter a valid PAN (e.g. ABCDE1234F)',
                  },
                })}
              />
              {errors.panNumber && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.panNumber.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                GST Number{' '}
                <span className="text-muted-foreground text-xs font-400">
                  (Optional)
                </span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Required if annual turnover &gt; ₹20 Lakh
              </p>
              <input
                type="text"
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground uppercase"
                {...register('gstNumber', {
                  pattern: {
                    value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                    message: 'Enter a valid 15-character GST number',
                  },
                })}
              />
              {errors.gstNumber && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.gstNumber.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Address Group */}
      <div className="bg-muted/30 rounded-xl p-4 border border-border">
        <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
          <Icon name="MapPinIcon" size={16} className="text-primary" />
          Business Address
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Street Address <span className="text-error">*</span>
            </label>
            <input
              type="text"
              placeholder="Shop No., Street, Area"
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
              {...register('businessAddress', {
                required: 'Business address is required',
              })}
            />
            {errors.businessAddress && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.businessAddress.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                City <span className="text-error">*</span>
              </label>
              <input
                type="text"
                placeholder="Mumbai"
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
                {...register('city', { required: 'City is required' })}
              />
              {errors.city && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.city.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                State <span className="text-error">*</span>
              </label>
              <select
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus"
                {...register('state', { required: 'State is required' })}
              >
                <option value="">Select state</option>
                {INDIAN_STATES.filter(Boolean).map((s) => (
                  <option key={`state-${s}`} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.state.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                Pincode <span className="text-error">*</span>
              </label>
              <input
                type="text"
                placeholder="400001"
                maxLength={6}
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
                {...register('pincode', {
                  required: 'Pincode is required',
                  pattern: {
                    value: /^[1-9][0-9]{5}$/,
                    message: 'Enter a valid 6-digit pincode',
                  },
                })}
              />
              {errors.pincode && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.pincode.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Banking Group */}
      <div className="bg-muted/30 rounded-xl p-4 border border-border">
        <h3 className="text-sm font-700 text-foreground mb-1 flex items-center gap-2">
          <Icon name="CreditCardIcon" size={16} className="text-primary" />
          Bank Account Details
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Your payouts will be transferred to this account. Ensure the account
          is in the name of the business/individual.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Account Number <span className="text-error">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter account number"
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
              {...register('bankAccountNumber', {
                required: 'Bank account number is required',
                minLength: { value: 9, message: 'Enter a valid account number' },
              })}
            />
            {errors.bankAccountNumber && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.bankAccountNumber.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                IFSC Code <span className="text-error">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                11-character code on your cheque book
              </p>
              <input
                type="text"
                placeholder="HDFC0001234"
                maxLength={11}
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground uppercase"
                {...register('bankIfsc', {
                  required: 'IFSC code is required',
                  pattern: {
                    value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                    message: 'Enter a valid 11-character IFSC code',
                  },
                })}
              />
              {errors.bankIfsc && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.bankIfsc.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                Bank Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                placeholder="HDFC Bank"
                className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
                {...register('bankName', {
                  required: 'Bank name is required',
                })}
              />
              {errors.bankName && (
                <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {errors.bankName.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Security notice */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3.5">
        <Icon name="ShieldCheckIcon" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          Your financial information is encrypted with 256-bit SSL and stored
          securely. Bozaat is PCI-DSS compliant. We never store your full bank
          credentials in plaintext.
        </p>
      </div>
    </div>
  );
}